/* eslint-disable */
import { Injectable } from '@angular/core';

import { IReferral, IGtmTrackingParams } from 'app/interfaces/profile.interfaces';

import { ELocalStorageKey } from '../enums/shared.enums';

declare global {
  interface Window {
    dataLayer: any[];
  }
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  dataLayer = window.dataLayer;

  trackPageLoaded(
    pageName: string,
    path: string,
    referrer: string,
    title: string,
    url: string,
    utmCampaign: string,
    utmContent: string,
    utmMedium: string,
    utmSource: string
  ): void {
    this.dataLayer.push({
      page_name: pageName,
      path: path,
      referrer: referrer,
      title: title,
      url: url,
      utm_campaign: utmCampaign,
      utm_content: utmContent,
      utm_medium: utmMedium,
      utm_source: utmSource,
      time_segment: new Date().toISOString()
    });
  }

  login(name: string, email: string, userId: string): void {
    this.dataLayer.push({
      event: 'login',
      eventModel: {
        name: name,
        email: email,
        userId: userId
      }
    });
  }

  signUp(userId: string, email: string): void {
    this.dataLayer.push({
      event: 'signUp',
      eventModel: {
        userId: userId,
        email: email
      }
    });
  }

  click(clickText: string, clickUrl: string, pageName: string, type: string, url: string): void {
    this.dataLayer.push({
      event: 'click',
      click_text: clickText,
      click_url: clickUrl,
      page_name: pageName,
      type: type,
      url: url,
      time_segment: new Date().toISOString()
    });
  }

  imageGenerated(
    creatorId: string,
    creatorName: string,
    userId: string,
    method: string,
    scene: string | null,
    activity: string | null,
    outfit: string | null,
    accessories: string | null,
    pose: string | null
  ): void {
    this.dataLayer.push({
      event: 'imageGenerated',
      eventModel: {
        creatorId: creatorId,
        creatorName: creatorName,
        userId: userId,
        method: method,
        scene: scene,
        activity: activity,
        outfit: outfit,
        accessories: accessories,
        pose: pose
      }
    });
  }

  chatInitiated(
    userId: string,
    userEmail: string,
    creatorName: string,
    chatId: string,
    creatorId: string
  ): void {
    this.dataLayer.push({
      event: 'chatInitiated',
      eventModel: {
        userId: userId,
        userEmail: userEmail,
        creatorName: creatorName,
        chatId: chatId,
        creatorId: creatorId
      }
    });
  }

  onboardingProfile(stepName: string, userId: string): void {
    if (localStorage.getItem(ELocalStorageKey.ProfileCompleted) === 'true') return;

    this.dataLayer.push({
      event: 'onboardingStepCompleted',
      eventModel: {
        stepName: stepName,
        userId: userId
      }
    });
  }

  voiceNotePlayed(userId: string, creatorName: string, chatState: string, creatorId: string): void {
    this.dataLayer.push({
      event: 'voiceNotePlayed',
      eventModel: {
        userId: userId,
        creatorName: creatorName,
        chatState: chatState,
        creatorId: creatorId
      }
    });
  }

  mediaClicked(
    userId: string,
    creatorName: string,
    chatState: string,
    creatorId: string,
    mediaId: string,
    mediaType: string
  ): void {
    this.dataLayer.push({
      event: 'mediaClick',
      eventModel: {
        chatState: chatState,
        creatorId: creatorId,
        creatorName: creatorName,
        userId: userId,
        mediaId: mediaId,
        mediaType: mediaType
      }
    });
  }

  paymentInitiated(userId: string, userEmail: string): void {
    this.dataLayer.push({
      event: 'paymentInitiated',
      eventModel: {
        userId: userId,
        userEmail: userEmail
      }
    });
  }

  onboardingStarted(
    userId: string,
    chatState: string,
    creatorId: string,
    creatorName: string
  ): void {
    this.dataLayer.push({
      event: 'onboardingStarted',
      eventModel: {
        chatState: chatState,
        creatorId: creatorId,
        creatorName: creatorName,
        userId: userId
      }
    });
  }

  getTrackingParams(): IReferral | null {
    const gtmData = this.parseTrackingCookie();
    if (!gtmData) return null;

    const referralData: IReferral = {
      utm_campaign: gtmData.campaign || '',
      utm_source: gtmData.source || '',
      utm_medium: gtmData.medium || '',
      utm_term: gtmData.term || '',
      utm_content: gtmData.content || '',
      referrer: gtmData.referrer || document.referrer || ''
    };

    return Object.values(referralData).some(Boolean) ? referralData : null;
  }

  private parseTrackingCookie(): IGtmTrackingParams | null {
    const cookies = document.cookie.split(';');
    const trackingCookie = cookies
      .find(cookie => cookie.trim().startsWith('tracking_params='))
      ?.trim()
      .substring('tracking_params='.length);

    if (!trackingCookie) return null;

    try {
      return JSON.parse(decodeURIComponent(trackingCookie)) as IGtmTrackingParams;
    } catch (error) {
      return null;
    }
  }
}
